import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useProduct } from "@/contexts/ProductContext";

export type IntegrationProvider = 
  | "stripe" 
  | "paypal" 
  | "mercadopago" 
  | "pagseguro" 
  | "hotmart" 
  | "kiwify" 
  | "pagarme" 
  | "vindi" 
  | "iugu" 
  | "eduzz";

export type IntegrationStatus = "connected" | "disconnected" | "error" | "pending";

export interface Integration {
  id: string;
  tenant_id: string;
  product_id: string | null;
  provider: IntegrationProvider;
  status: IntegrationStatus;
  credentials: any;
  webhook_secret: string | null;
  last_sync_at: string | null;
  sync_status: string | null;
  config: any;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface IntegrationLog {
  id: string;
  integration_id: string;
  event_type: string;
  status: "success" | "error" | "pending";
  request_data: any;
  response_data: any;
  error_message: string | null;
  created_at: string;
}

export const useIntegrations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { selectedProduct } = useProduct();

  const { data: integrations, isLoading, error } = useQuery({
    queryKey: ["integrations", selectedProduct?.id],
    queryFn: async () => {
      let query = supabase
        .from("integrations")
        .select("*")
        .order("created_at", { ascending: false });

      if (selectedProduct?.id) {
        query = query.eq("product_id", selectedProduct.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Integration[];
    },
  });

  const connectMutation = useMutation({
    mutationFn: async ({
      provider,
      credentials,
      productId,
    }: {
      provider: IntegrationProvider;
      credentials: any;
      productId?: string;
    }) => {
      // Get tenant_id from user_roles
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const { data: userRole } = await supabase
        .from("user_roles")
        .select("tenant_id")
        .eq("user_id", user.id)
        .single();

      if (!userRole?.tenant_id) throw new Error("Tenant não encontrado");

      const { data, error } = await supabase
        .from("integrations")
        .insert([{
          tenant_id: userRole.tenant_id,
          provider: provider,
          credentials,
          product_id: productId || null,
          status: "connected",
          config: {},
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast({
        title: "Integração conectada",
        description: "A integração foi configurada com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao conectar",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("integrations")
        .update({ status: "disconnected" })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast({
        title: "Integração desconectada",
        description: "A integração foi desconectada com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao desconectar",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("integrations")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast({
        title: "Integração removida",
        description: "A integração foi removida com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao remover",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const syncMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("integrations")
        .update({ 
          last_sync_at: new Date().toISOString(),
          sync_status: "syncing" 
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      toast({
        title: "Sincronização iniciada",
        description: "Os dados estão sendo sincronizados.",
      });
    },
  });

  const getIntegrationLogs = async (integrationId: string, limit = 50) => {
    const { data, error } = await supabase
      .from("integration_logs")
      .select("*")
      .eq("integration_id", integrationId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data as IntegrationLog[];
  };

  return {
    integrations: integrations || [],
    isLoading,
    error,
    connectIntegration: connectMutation.mutate,
    disconnectIntegration: disconnectMutation.mutate,
    deleteIntegration: deleteMutation.mutate,
    syncIntegration: syncMutation.mutate,
    getIntegrationLogs,
  };
};
